import React, { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import BookingModal from "./BookingModal";
import { trackEvent } from "../lib/analytics";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SERVICES = [
  {
    id: "intuitive_insight",
    title: "Intuitive Guidance",
    tagline: "Clarity, on demand.",
    description:
      "A spiritually grounded session offering intuitive insight and guidance to help you navigate life's challenges with clarity and confidence. Through soul-level perspective and subconscious awareness, discover deeper understanding and practical next steps.",
    image: "https://static.prod-images.emergentagent.com/jobs/9bed6362-dfc5-47b0-81cd-4add044bd986/images/e1e2ec0b29e74134bc365775f329836dfff358357f250cc750d313bf0038030f.png",
    duration: "45 minutes",
    price: 125,
    bullets: ["Clarity on a current challenge", "Intuitive insight", "Practical next step guidance"],
  },
  {
    id: "past_life",
    title: "Past Life Regression Therapy",
    tagline: "Cross the veil of memory.",
    description:
      "A guided journey into the subconscious to explore past life memories influencing your current patterns, relationships, and emotions. Gain insight, release what no longer serves you, and reconnect with your soul's broader journey.",
    image: "https://static.prod-images.emergentagent.com/jobs/9bed6362-dfc5-47b0-81cd-4add044bd986/images/54d096c00af02d366bc6198b197a326b43c51505be85c2ae2088d4a01d30f2b4.png",
    duration: "2 hours",
    price: 325,
    bullets: ["Release karmic patterns", "Heal recurring relationships", "Reconnect with soul memory"],
  },
  {
    id: "soul_regression",
    title: "Soul Regression Therapy",
    tagline: "Beyond this lifetime.",
    description:
      "An expansive experience accessing the space between lives to connect with your higher self, soul guides, and deeper purpose. Receive clarity, perspective, and a profound sense of alignment.",
    image: "https://static.prod-images.emergentagent.com/jobs/9bed6362-dfc5-47b0-81cd-4add044bd986/images/bc0b8e3854cd604c85dc6c68ff7876fc905e0449c2fdf055c47fd2f8f5f79491.png",
    duration: "3 hours",
    price: 525,
    bullets: ["Meet your higher self", "Receive guidance from soul guides", "Anchor your soul's purpose"],
  },
];

export default function ServicesSection() {
  const [active, setActive] = useState(null);
  const [prices, setPrices] = useState({});

  useEffect(() => {
    axios.get(`${API}/packages`)
      .then((r) => {
        const map = {};
        (r.data || []).forEach((p) => { map[p.id] = p.amount; });
        setPrices(map);
      })
      .catch(() => {});
  }, []);

  const services = SERVICES.map((s) => ({ ...s, price: prices[s.id] ?? s.price }));
  const minPrice = Math.min(...services.map((s) => s.price));

  return (
    <section
      id="sessions"
      data-testid="services-section"
      className="relative py-20 md:py-32 lg:py-40 px-5 md:px-12"
    >
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.7 }}
          className="text-center max-w-3xl mx-auto mb-20"
        >
          <p className="font-sans text-xs tracking-overline text-[#D4AF37] mb-4">02 — private sessions</p>
          <h2 className="font-serif text-4xl sm:text-5xl md:text-6xl text-white tracking-tight">
            Step into the <span className="italic gold-text">work.</span>
          </h2>
          <p className="mt-6 font-sans text-base sm:text-lg text-slate-300 leading-relaxed">
            Three intimate, deeply held sessions — delivered securely online via encrypted telehealth.
            <span className="block mt-2 text-slate-400">From <span className="text-[#D4AF37]">${minPrice} AUD</span> · Alternative payment options available upon request.</span>
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {SERVICES.map((s, i) => (
            <motion.article
              key={s.id}
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.7, delay: i * 0.1 }}
              className="glass-card rounded-3xl overflow-hidden flex flex-col"
              data-testid={`service-card-${s.id}`}
            >
              <div className="relative aspect-[4/3] overflow-hidden">
                <img
                  src={s.image}
                  alt={s.title}
                  className="w-full h-full object-cover transition-transform duration-700 hover:scale-105"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#040508] via-[#040508]/40 to-transparent" />
                <div className="absolute bottom-4 left-4 right-4">
                  <p className="font-sans text-[10px] uppercase tracking-overline text-[#D4AF37]">{s.duration}</p>
                  <p className="mt-1 font-serif text-2xl text-white italic">{s.tagline}</p>
                </div>
              </div>
              <div className="p-7 flex flex-col flex-1">
                <h3 className="font-serif text-2xl text-white">{s.title}</h3>
                <p className="mt-3 font-sans text-sm text-slate-300 leading-relaxed">{s.description}</p>
                <ul className="mt-5 space-y-2">
                  {s.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-2 text-sm text-slate-300 font-sans">
                      <span className="mt-1 w-1 h-1 rounded-full bg-[#D4AF37] shrink-0" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-7 pt-6 border-t border-white/5 flex items-center justify-between">
                  <p className="font-serif text-2xl text-white">${s.price} AUD</p>
                  <button
                    data-testid={`book-button-${s.id}`}
                    onClick={() => { trackEvent("service_clicked", { service_id: s.id }); trackEvent("booking_opened", { service_id: s.id }); setActive(s); }}
                    className="btn-gold rounded-full px-6 py-3 text-xs flex items-center gap-2"
                  >
                    Book a session
                    <ArrowUpRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </motion.article>
          ))}
        </div>
      </div>

      <BookingModal
        open={!!active}
        service={active}
        onClose={() => setActive(null)}
      />
    </section>
  );
}
